<template>
    <app-dialog v-model="isOpen" size="md" append-to-body @close="onClose">
        <template #header>
            <h4 class="text-lg font-semibold text-theme-base">Imagen</h4>
            <p class="mt-1 text-sm text-theme-muted">
                Elige con qué tamaño y en qué posición se publica.
            </p>
        </template>

        <div class="flex flex-col gap-3">
            <div
                v-if="isOpen"
                class="rounded-lg border border-theme-border px-3 py-2"
            >
                <span class="text-xs text-theme-muted">Vista previa</span>
                <div class="markdown-body mt-1" v-html="previewHtml"></div>
            </div>

            <div>
                <span class="block text-sm font-semibold text-theme-muted">
                    Tamaño
                </span>
                <div class="mt-1 flex flex-wrap gap-1">
                    <app-button
                        v-for="size in sizes"
                        :key="size.key"
                        type="button"
                        size="small"
                        :relief="size.key !== sizeKey"
                        @click="sizeKey = size.key"
                    >
                        {{ size.label }}
                    </app-button>
                </div>
            </div>

            <custom-input
                v-if="isCustomSize"
                v-model="customWidth"
                type="number"
                label="Ancho en píxeles"
                placeholder="320"
            />

            <div>
                <span class="block text-sm font-semibold text-theme-muted">
                    Posición
                </span>
                <alignment-picker v-model="align" class="mt-1" />
            </div>

            <custom-input
                v-model="alt"
                label="Texto alternativo"
                placeholder="Describe la imagen"
            />

            <custom-input
                v-model="link"
                label="Enlace al hacer clic (opcional)"
                placeholder="https://"
            />

            <p v-if="error" class="text-xs text-theme-danger">{{ error }}</p>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <app-button type="button" @click="submit">
                    Insertar imagen
                </app-button>
            </div>
        </template>
    </app-dialog>
</template>

<script>
import { renderMarkdown } from "@/common/markdown/renderMarkdown";

import AppButton from "@/components/utils/AppButton.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

import AlignmentPicker from "./AlignmentPicker.vue";
import { URL_PATTERN } from "./blocks";
import {
    IMAGE_SIZES,
    MAX_IMAGE_WIDTH,
    MIN_IMAGE_WIDTH,
    buildImageSnippet
} from "./images";

const DEFAULT_SIZE_KEY = "medium";

const DEFAULT_ALIGNMENT = "center";

export default {
    name: "ImageBlockDialog",
    components: {
        AlignmentPicker,
        AppButton,
        AppDialog,
        CustomInput
    },
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            default: () => false,
            type: Boolean,
            required: true
        },
        image: {
            default: () => null,
            type: Object,
            required: false
        }
    },
    emits: ["input", "submit"],
    data: () => ({
        align: DEFAULT_ALIGNMENT,
        alt: "",
        customWidth: "",
        error: "",
        link: "",
        sizeKey: DEFAULT_SIZE_KEY
    }),
    computed: {
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        sizes() {
            return IMAGE_SIZES;
        },
        isCustomSize() {
            return this.sizeKey === "custom";
        },
        width() {
            if (this.isCustomSize) {
                const typed = Number(this.customWidth);
                return Number.isFinite(typed) && typed > 0 ? typed : null;
            }
            const size = IMAGE_SIZES.find((item) => item.key === this.sizeKey);
            return size ? size.width : null;
        },
        options() {
            return {
                align: this.align,
                alt: this.alt.trim(),
                link: this.link.trim(),
                url: (this.image && this.image.url) || "",
                width: this.width
            };
        },
        previewHtml() {
            if (!this.options.url) return "";
            return renderMarkdown(buildImageSnippet(this.options).text);
        }
    },
    watch: {
        value(isOpen) {
            if (isOpen) this.reset();
        },
        sizeKey() {
            this.error = "";
        }
    },
    created() {
        this.reset();
    },
    methods: {
        reset() {
            this.align = DEFAULT_ALIGNMENT;
            this.alt = (this.image && this.image.alt) || "";
            this.customWidth = "";
            this.error = "";
            this.link = "";
            this.sizeKey = DEFAULT_SIZE_KEY;
        },
        submit() {
            const { link, url, width } = this.options;
            if (!url) {
                this.error = "Elige primero una imagen";
                return;
            }
            if (this.isCustomSize && !width) {
                this.error = "Escribe el ancho en píxeles";
                return;
            }
            if (
                this.isCustomSize &&
                (width < MIN_IMAGE_WIDTH || width > MAX_IMAGE_WIDTH)
            ) {
                this.error = `El ancho debe estar entre ${MIN_IMAGE_WIDTH} y ${MAX_IMAGE_WIDTH} píxeles`;
                return;
            }
            if (link && !URL_PATTERN.test(link)) {
                this.error =
                    "Escribe un enlace completo, que empiece por http:// o https://";
                return;
            }
            this.error = "";
            this.$emit("submit", this.options);
        },
        onClose() {
            this.error = "";
        }
    }
};
</script>
